// store global types
import type { TCreateSlice } from "@/store";

// dependencies types
import type { TSliceTableExtractions } from "../tableExtractions/sliceTableExtractions";
import type { TOptionWithType } from "../accounts/accountTypes";
import type {
    TLLMValidationSummary,
    TLLMDiagnosisSummary,
} from "@/apis/odin/apiODIN";

// internal slice types
import type { TDtoTableExtraction } from "../tableExtractions/TDtoTableExtraction";

// selectors
import {
    createSelectorTableMappingsWithAppliedChangesAndOmittedRows,
    createSelectorTableMappingsErrorRows,
} from "./createSelectorsTableMappings";
import { createStandardizedTableErrorRows } from "./createStandardizedTableErrorRows";

type ErrorType = "missingMapping" | "dataOutlier" | "llmValidation";

export type ErrorRow = {
    rowId: number | string;
    colId: string;
    periodId?: number;
    columnName?: string;
    accountName: string;
    errorType: ErrorType;
    errorValue?: number;
    severity: "warning" | "error";
    threshold?: number;
    direction?: "small" | "large";
    resolved?: boolean;
    dismissed?: boolean;
    checkType?: string;
    expected?: string;
    actual?: string;
    diff?: string;
    diagnosisReason?: string;
    diagnosisFix?: string | null;
    diagnosisUserAction?: string;
    diagnosisTargetRowIds?: Array<number | string>;
    // Opaque handle echoed back to dismiss the diagnosis group.
    diagnosisCheckIdentities?: string[];
    diagnosisDismissed?: boolean;
};

export type SelectedColumn = {
    fileName: string;
    columnGroup: string;
    status: "active" | "inactive" | "partial";
    currentUpload: boolean;
};

type TDerivedStateTableMappings = {
    getTableData: () => (state: {
        rowsOmitted: TStateTableMappings["rowsOmitted"];
        rowsWithMappingsAltered: TStateTableMappings["rowsWithMappingsAltered"];
        optionsAccounts: TOptionWithType[];
    }) => TDtoTableExtraction | null;
    getErrors: () => (state: {
        rowsOmitted: TStateTableMappings["rowsOmitted"];
        rowsWithMappingsAltered: TStateTableMappings["rowsWithMappingsAltered"];
        optionsAccounts: TOptionWithType[];
        llmValidation: TLLMValidationSummary | null;
        llmDiagnosis: TLLMDiagnosisSummary | null;
    }) => { [key: string]: ErrorRow[] };
    getOutliers: () => (state: {
        rowsDismissed: TStateTableMappings["rowsDismissed"];
    }) => { [key: string]: ErrorRow[] };
    getActivePeriods: (state: {
        extractionStandardizedActiveColumns: TSliceTableExtractions["extractionStandardizedActiveColumns"];
        extractionStandardizedMonthLabelToPeriodIdMap: TSliceTableExtractions["extractionStandardizedMonthLabelToPeriodIdMap"];
    }) => {
        extractionId: number;
        periodId: number;
    }[];
};

type TActionsPublicTableMapping = {
    resetStateTableMapping: () => void;
    setRowsAlreadyAltered: ({
        rows,
    }: {
        rows: TStateTableMappings["rowsWithMappingsAltered"];
    }) => void;
    setRowsAlreadyOmitted: ({
        rows,
    }: {
        rows: TStateTableMappings["rowsOmitted"];
    }) => void;
    setRowsAlreadyDismissed: ({
        rows,
    }: {
        rows: TStateTableMappings["rowsDismissed"];
    }) => void;
    omitRow: ({ rowId }: { rowId: number }) => void;
    changeMapping: (params: {
        rowId: number;
        newValue: string;
        accountOptions: TOptionWithType[];
    }) => void;
    setHighlightedCell: (
        rowId: number | string | null,
        colId: string | null,
    ) => void;
    setHighlightedTargetRowIds: (rowIds: Array<number | string>) => void;
    onDismissWarnings: (
        warnings: {
            rowId: number;
            periodId: number;
            accountLabel: string;
        }[],
    ) => void;
    setConvertToDraftModalOpenState: (open: boolean) => void;
    setSaveExtractionCompleteModalOpenState: (open: boolean) => void;
    setCloseExtractionModalOpenState: (open: boolean) => void;
    setLabelsetSourceId: ({
        labelsetSourceId,
    }: {
        labelsetSourceId: number | null;
    }) => void;
};

export type TSliceTableMappings = TStateTableMappings &
    TActionsPublicTableMapping &
    TDerivedStateTableMappings;

export type TStateTableMappings = {
    highlightedCell: {
        rowId: number | string | null;
        colId: string | null;
    } | null;
    highlightedTargetRowIds: Array<number | string>;
    rowsWithMappingsAltered: {
        rowId: number;
        type?: string;
        account: string;
        reverted: boolean;
    }[];
    rowsOmitted: { rowId: number }[];
    rowsDismissed: { rowId: number; periodId: number; accountLabel: string }[];
    convertToDraftModalOpen: boolean;
    saveExtractionCompleteModalOpen: boolean;
    closeExtractionModalOpen: boolean;
    labelsetSourceId: number | null;
};

export const initialStateTableMappings: TStateTableMappings = {
    highlightedCell: null,
    highlightedTargetRowIds: [],
    rowsWithMappingsAltered: [],
    rowsOmitted: [],
    rowsDismissed: [],
    convertToDraftModalOpen: false,
    saveExtractionCompleteModalOpen: false,
    closeExtractionModalOpen: false,
    labelsetSourceId: null,
};

export const createSliceTableMappings: TCreateSlice<
    TSliceTableMappings,
    {
        getExtractedRows: () => TSliceTableExtractions["extractionRowsLoaded"];
        getStandardizedExtractionErrors: () => TSliceTableExtractions["extractionStandardizedErrors"];
    }
> =
    ({ getExtractedRows, getStandardizedExtractionErrors }) =>
    (set, getState) => {
        const actionsPublic: TActionsPublicTableMapping = {
            setConvertToDraftModalOpenState: (open: boolean) => {
                set(
                    state => ({
                        ...state,
                        convertToDraftModalOpen: open,
                    }),
                    false,
                    "tableMapping/setConvertToDraftModalOpenState",
                );
            },
            setSaveExtractionCompleteModalOpenState: (open: boolean) => {
                set(
                    state => ({
                        ...state,
                        saveExtractionCompleteModalOpen: open,
                    }),
                    false,
                    "tableMapping/setSaveExtractionCompleteModalOpenState",
                );
            },
            setCloseExtractionModalOpenState: (open: boolean) => {
                set(
                    state => ({
                        ...state,
                        closeExtractionModalOpen: open,
                    }),
                    false,
                    "tableMapping/setCloseExtractionModalOpenState",
                );
            },
            setHighlightedCell: (rowId, colId) => {
                set(
                    state => {
                        const prevRowId = state.highlightedCell?.rowId ?? null;
                        const rowChanged = prevRowId !== rowId;
                        // Only clear target highlights when the row changes AND
                        // there is something to clear. Allocating a fresh []
                        // every click churns the reference, which re-renders the
                        // mappings view and tears down an open account select.
                        const shouldClearTargets =
                            rowChanged &&
                            state.highlightedTargetRowIds.length > 0;
                        return {
                            ...state,
                            highlightedCell: { rowId, colId },
                            highlightedTargetRowIds: shouldClearTargets
                                ? []
                                : state.highlightedTargetRowIds,
                        };
                    },
                    false,
                    "tableMapping/setHighlightedCell",
                );
            },
            setHighlightedTargetRowIds: rowIds => {
                set(
                    state => ({
                        ...state,
                        highlightedTargetRowIds: rowIds,
                    }),
                    false,
                    "tableMapping/setHighlightedTargetRowIds",
                );
            },
            onDismissWarnings: (
                warnings: {
                    rowId: number;
                    periodId: number;
                    accountLabel: string;
                }[],
            ) => {
                set(
                    state => {
                        const newWarnings = warnings.filter(
                            warning =>
                                !state.rowsDismissed.some(
                                    dismissed =>
                                        dismissed.rowId === warning.rowId &&
                                        dismissed.periodId ===
                                            warning.periodId &&
                                        dismissed.accountLabel ===
                                            warning.accountLabel,
                                ),
                        );

                        const updatedDismissed = warnings.some(warning =>
                            state.rowsDismissed.some(
                                dismissed =>
                                    dismissed.rowId === warning.rowId &&
                                    dismissed.periodId === warning.periodId &&
                                    dismissed.accountLabel ===
                                        warning.accountLabel,
                            ),
                        )
                            ? state.rowsDismissed.filter(
                                  dismissed =>
                                      !warnings.some(
                                          warning =>
                                              warning.rowId ===
                                                  dismissed.rowId &&
                                              warning.periodId ===
                                                  dismissed.periodId &&
                                              warning.accountLabel ===
                                                  dismissed.accountLabel,
                                      ),
                              )
                            : [...state.rowsDismissed, ...newWarnings];

                        return {
                            ...state,
                            rowsDismissed: updatedDismissed,
                        };
                    },
                    false,
                    "tableMapping/onAcceptWarning",
                );
            },
            resetStateTableMapping: () => {
                set(
                    initialStateTableMappings,
                    false,
                    "tableMapping/resetState",
                );
            },

            setRowsAlreadyAltered: ({ rows }) => {
                set(
                    () => ({
                        rowsWithMappingsAltered: rows,
                    }),
                    false,
                    "tableMapping/setRowsAlreadyAltered",
                );
            },
            setRowsAlreadyOmitted: ({ rows }) => {
                set(
                    () => ({
                        rowsOmitted: rows,
                    }),
                    false,
                    "tableMapping/setRowsAlreadyOmitted",
                );
            },
            setRowsAlreadyDismissed: ({ rows }) => {
                set(
                    () => ({
                        rowsDismissed: rows,
                    }),
                    false,
                    "tableMapping/setRowsAlreadyDismissed",
                );
            },

            setLabelsetSourceId: ({ labelsetSourceId }) => {
                set(
                    () => ({
                        labelsetSourceId: labelsetSourceId,
                    }),
                    false,
                    "tableMapping/setLabelsetSourceId",
                );
            },

            omitRow: ({ rowId }: { rowId: number }) => {
                const state = getState();
                const extractedRows = getExtractedRows();

                if (!extractedRows) {
                    // @TODO move error handling to other slice
                    console.error("Cannot omit row, no table data loaded");
                    return;
                }

                const row = extractedRows.rows.find(row => row.rowId === rowId);
                if (!row) {
                    // @TODO move error handling to other slice
                    console.error(
                        `Cannot omit row, row with id ${rowId} not found`,
                    );
                    return;
                }

                const rowsWithMappingsAlteredWithoutRowPendingToBeOmitted =
                    state.rowsWithMappingsAltered.filter(
                        row => row.rowId !== rowId,
                    );
                const isRowAlreadyOmitted = state.rowsOmitted.some(
                    row => row.rowId === rowId,
                );

                if (isRowAlreadyOmitted) return;

                set(
                    {
                        rowsWithMappingsAltered:
                            rowsWithMappingsAlteredWithoutRowPendingToBeOmitted,
                        rowsOmitted: [...state.rowsOmitted, { rowId }],
                    },
                    false,
                    "tableMapping/omitRow",
                );
            },

            changeMapping: ({
                rowId,
                newValue,
                accountOptions,
            }: {
                rowId: number;
                newValue: string;
                accountOptions: TOptionWithType[];
            }) => {
                const state = getState();
                const extractedRows = getExtractedRows();

                if (!extractedRows) {
                    // @TODO move error handling to other slice
                    console.error(
                        "Cannot change mapping, no table data loaded",
                    );
                    return;
                }

                const originalRow = extractedRows.rows.find(
                    row => row.rowId === rowId,
                );
                if (!originalRow) {
                    // @TODO move error handling to other slice
                    console.error(
                        `Cannot change mapping, row with id ${rowId} not found`,
                    );
                    return;
                }
                const originalAccount = originalRow.account;

                const newOption = accountOptions.find(
                    option => option.value === newValue,
                );

                const originalOption =
                    originalAccount != null
                        ? accountOptions.find(
                              option => option.value === originalAccount,
                          )
                        : null;
                const originalAccountIsMapped =
                    originalRow.mappingType === "mapped";
                if (originalAccountIsMapped && !originalOption) {
                    // @TODO move error handling to other slice
                    console.error(
                        `Cannot change mapping, original account with code: \`${originalAccount}\` not found in available accounts`,
                    );
                    return;
                }
                if (!newOption) {
                    // @TODO move error handling to other slice
                    console.error(
                        `Cannot change mapping, new account with code: \`${newValue}\` not found in available accounts`,
                    );
                    return;
                }

                const omittedRowsWithoutPendingRow = state.rowsOmitted.filter(
                    row => row.rowId !== rowId,
                );
                const storedChangesWithoutPendingRow =
                    state.rowsWithMappingsAltered.filter(
                        change => change.rowId !== rowId,
                    );

                const accountChanged = originalAccount !== newValue;
                const accountReverted =
                    (state.rowsOmitted.some(row => row.rowId === rowId) ||
                        state.rowsWithMappingsAltered.some(
                            row => row.rowId === rowId,
                        )) &&
                    originalAccount === newValue;

                if (!accountChanged && !accountReverted) {
                    set(
                        {
                            rowsWithMappingsAltered:
                                storedChangesWithoutPendingRow,
                            rowsOmitted: omittedRowsWithoutPendingRow,
                        },
                        false,
                        "tableMapping/changeMapping",
                    );
                    return;
                }

                const pendingChange = accountReverted
                    ? {
                          rowId,
                          account: newValue,
                          reverted: true,
                      }
                    : {
                          rowId,
                          account: newValue,
                          reverted: false,
                      };

                set(
                    {
                        rowsWithMappingsAltered: [
                            ...storedChangesWithoutPendingRow,
                            pendingChange,
                        ],
                        rowsOmitted: omittedRowsWithoutPendingRow,
                    },
                    false,
                    "tableMapping/changeMapping",
                );
            },
        };

        const derivedState: TDerivedStateTableMappings = {
            getTableData() {
                return ({
                    rowsOmitted,
                    rowsWithMappingsAltered,
                    optionsAccounts,
                }) => {
                    const rowsLoaded = getExtractedRows();
                    return createSelectorTableMappingsWithAppliedChangesAndOmittedRows()(
                        {
                            rowsExtracted: rowsLoaded,
                            rowsOmitted: rowsOmitted,
                            rowsWithMappingsAltered: rowsWithMappingsAltered,
                            optionsAccounts,
                        },
                    );
                };
            },
            getErrors:
                () =>
                ({
                    rowsOmitted,
                    rowsWithMappingsAltered,
                    optionsAccounts,
                    llmValidation,
                    llmDiagnosis,
                }) => {
                    const rowsLoaded = getExtractedRows();
                    const rowsLoadedWithChangesApplied =
                        createSelectorTableMappingsWithAppliedChangesAndOmittedRows()(
                            {
                                rowsExtracted: rowsLoaded,
                                rowsOmitted: rowsOmitted,
                                rowsWithMappingsAltered:
                                    rowsWithMappingsAltered,
                                optionsAccounts,
                            },
                        );
                    const mappingErrors =
                        createSelectorTableMappingsErrorRows()({
                            rowsLoaded,
                            rowsLoadedWithChangesApplied,
                        });

                    if (
                        llmValidation === null ||
                        llmValidation.issues.length === 0
                    ) {
                        return mappingErrors;
                    }

                    const columnDefs =
                        rowsLoaded?.columnDefsOperatingStatements ?? [];
                    const totalRows = rowsLoaded?.totalRows ?? [];

                    const llmErrorGroups = llmValidation.issues.reduce<{
                        [key: string]: ErrorRow[];
                    }>((acc, issue) => {
                        if (
                            issue.checkType === "net_income" ||
                            issue.severity === "warning"
                        ) {
                            return acc;
                        }
                        const totalRow = totalRows.find(
                            r => r.totalName === issue.totalRowName,
                        );
                        const rowId = totalRow?.rowId ?? issue.totalRowName;
                        const colDef = columnDefs[issue.columnIndex];
                        const colId =
                            colDef?.field ?? `col_${issue.columnIndex}`;
                        const groupKey = `${rowId},${issue.severity}`;
                        const diagGroup = llmDiagnosis?.groups.find(g =>
                            g.issueIds.includes(issue.issueId),
                        );
                        const targetRowIds: Array<number | string> =
                            diagGroup?.targetRowIds ?? [];
                        const errorRow: ErrorRow = {
                            rowId,
                            colId,
                            accountName: issue.totalRowName,
                            errorType: "llmValidation",
                            severity: issue.severity as "warning" | "error",
                            columnName: issue.columnLabel,
                            errorValue: parseFloat(issue.diff),
                            checkType: issue.checkType,
                            expected: issue.expected,
                            actual: issue.actual,
                            diff: issue.diff,
                            diagnosisReason: diagGroup?.reason,
                            diagnosisFix: diagGroup?.suggestedUserFix,
                            diagnosisUserAction: diagGroup?.userAction,
                            diagnosisTargetRowIds: targetRowIds,
                            diagnosisCheckIdentities:
                                diagGroup?.checkIdentities ?? undefined,
                            diagnosisDismissed:
                                diagGroup?.dismissed ?? undefined,
                            // Reuses the warnings' dismissed greying.
                            dismissed: diagGroup?.dismissed ?? undefined,
                        };
                        return {
                            ...acc,
                            [groupKey]: [...(acc[groupKey] ?? []), errorRow],
                        };
                    }, {});

                    return { ...mappingErrors, ...llmErrorGroups };
                },
            getOutliers: () => state => {
                return createStandardizedTableErrorRows()({
                    standardizedExtractionErrors:
                        getStandardizedExtractionErrors(),
                    rowsDismissed: state.rowsDismissed,
                });
            },
            getActivePeriods: state => {
                const activeColumns = state.extractionStandardizedActiveColumns;
                const monthToPeriodMap =
                    state.extractionStandardizedMonthLabelToPeriodIdMap;
                return Object.entries(activeColumns ?? {})
                    .map(([key, value]) => {
                        const periodId = monthToPeriodMap?.[key];
                        if (periodId === undefined) return null;
                        return {
                            extractionId: value.to,
                            periodId,
                        };
                    })
                    .filter(
                        (
                            period,
                        ): period is {
                            extractionId: number;
                            periodId: number;
                        } => period !== null,
                    );
            },
        };

        return {
            ...initialStateTableMappings,
            ...actionsPublic,
            ...derivedState,
        };
    };
